import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChefHat, Bell, ShoppingBag, XCircle, Clock, BellRing, Share2, History } from 'lucide-react';
import { db } from '../src/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { useSettings } from '../src/contexts/SettingsContext';
import { t } from '../translations';
import type { Order, OrderStatus } from '../types';
import { toast } from 'sonner';
import { orderSchema, parseFirestoreDoc } from '../src/utils/firestoreSchemas';
import { lineTotal } from '../src/utils/orderItems';

// 4 шага в порядке прогресса. cancelled — отдельный финальный экран,
// не входит в линейную прогрессию.
const PROGRESS: OrderStatus[] = ['new', 'accepted', 'ready', 'completed'];

const OrderTracker: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, loading: userLoading } = useUser();
  const { settings } = useSettings();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  // Запоминаем предыдущий статус, чтобы дёрнуть push именно в момент перехода
  // ready, а не на каждом re-render когда статус уже ready.
  const prevStatusRef = useRef<OrderStatus | null>(null);

  useEffect(() => {
    if (!id || userLoading) return;
    if (!user) {
      // Firebase Auth ещё не успел подтянуть anonymous-сессию.
      // Listener Firestore не подключится без auth, поэтому ждём пока придёт user.
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'orders', id),
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const parsed = parseFirestoreDoc<Omit<Order, 'id'>>('order', snap.data(), orderSchema);
        setOrder({ id: snap.id, ...parsed } as Order);
        setLoading(false);
      },
      (err) => {
        console.error('OrderTracker snapshot error:', err?.message || 'unknown');
        setNotFound(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [id, user, userLoading]);

  // Push-нотификация при переходе ready: только один раз за жизнь компонента.
  useEffect(() => {
    if (!order) return;
    const prev = prevStatusRef.current;
    prevStatusRef.current = order.status;
    if (prev && prev !== 'ready' && order.status === 'ready') {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification(t('tracker.readyNotificationTitle', language), {
            body: t('tracker.readyNotificationBody', language),
            icon: '/favicon.png',
            tag: `order-${order.id}`,
          });
        } catch {
          // Safari/iOS могут не дать создать без service worker — ловим тихо.
        }
      }
      toast.success(t('tracker.readyNotificationTitle', language));
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
    }
  }, [order, language]);

  const requestNotifications = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') toast.success(t('tracker.notificationsOn', language));
  };

  if (loading || userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="w-10 h-10 border-4 border-[var(--accent-color)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[var(--bg-card)] rounded-3xl shadow-xl p-8 text-center border border-[var(--glass-border)]">
          <div className="w-20 h-20 bg-[var(--neutral-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-[var(--text-secondary)]" />
          </div>
          <h1 className="text-2xl font-black text-[var(--text-primary)] mb-2 uppercase">
            {t('tracker.notFound', language)}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full bg-[var(--accent-color)] text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-5 h-5" />
            {t('checkout.backToMenu', language)}
          </button>
        </div>
      </div>
    );
  }

  const isCancelled = order.status === 'cancelled';
  const currentIndex = isCancelled ? -1 : Math.max(0, PROGRESS.indexOf(order.status));

  const stepText: Record<OrderStatus, string> = {
    new: t('tracker.waitingForKitchen', language),
    accepted: t('tracker.inKitchen', language),
    ready: t('tracker.readyForPickup', language),
    completed: t('tracker.served', language),
    cancelled: t('tracker.cancelled', language),
  };

  const stepIcons = [
    <Bell key="new" className="w-5 h-5" />,
    <ChefHat key="accepted" className="w-5 h-5" />,
    <Clock key="ready" className="w-5 h-5" />,
    <Check key="completed" className="w-5 h-5" />,
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] p-4 md:p-8 flex items-start md:items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-[var(--bg-card)] rounded-3xl shadow-xl border border-[var(--glass-border)] overflow-hidden my-auto"
      >
        <div className="p-6 bg-[var(--neutral-bg)] border-b border-[var(--glass-border)] text-center">
          {/* Hero-emoji: меняется с анимацией при переходе статуса. Даёт «жизнь»
              экрану ожидания. AnimatePresence по order.status — key, чтобы
              был exit+enter при смене. */}
          <AnimatePresence mode="wait">
            <motion.div
              key={order.status}
              initial={{ scale: 0.5, opacity: 0, rotate: -15 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0.5, opacity: 0, rotate: 15 }}
              transition={{ type: 'spring', stiffness: 280, damping: 18 }}
              className="text-7xl mb-3 inline-block"
              aria-hidden
            >
              {({
                new: '🔔',
                accepted: '👨‍🍳',
                ready: '🍽',
                completed: '✨',
                cancelled: '🚫',
              } as Record<typeof order.status, string>)[order.status]}
            </motion.div>
          </AnimatePresence>
          <p className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
            {t('checkout.orderNumber', language)} {order.orderNumber || order.id.slice(-4)}
          </p>
          <h1 className="text-2xl font-black text-[var(--text-primary)] mt-1">
            {t('tracker.title', language)}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 font-medium">
            {stepText[order.status]}
          </p>
        </div>

        {/* Прогресс */}
        {!isCancelled && (
          <div className="p-6 border-b border-[var(--glass-border)]">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-5 left-5 right-5 h-1 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[var(--accent-color)]"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentIndex / (PROGRESS.length - 1)) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              {PROGRESS.map((s, i) => {
                const reached = i <= currentIndex;
                const active = i === currentIndex;
                return (
                  <div key={s} className="relative z-10 flex flex-col items-center gap-1">
                    <motion.div
                      animate={{ scale: active ? 1.1 : 1 }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        reached
                          ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white'
                          : 'bg-[var(--bg-card)] border-[var(--neutral-bg)] text-[var(--text-secondary)]'
                      } ${active ? 'shadow-lg shadow-[var(--accent-20)]' : ''}`}
                    >
                      {stepIcons[i]}
                    </motion.div>
                    <span
                      className={`text-[9px] font-black uppercase tracking-tight ${
                        reached ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
                      }`}
                    >
                      {t(`cashier.status.${s}`, language)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isCancelled && (
          <div className="p-6 border-b border-[var(--glass-border)] flex items-center gap-4">
            <div className="w-12 h-12 bg-[var(--danger-bg-50)] rounded-full flex items-center justify-center">
              <XCircle className="w-6 h-6 text-[var(--color-danger-fg)]" />
            </div>
            <p className="font-bold text-[var(--text-primary)]">
              {t('tracker.cancelled', language)}
            </p>
          </div>
        )}

        {/* Notify-кнопка показываем только пока заказ не готов */}
        <AnimatePresence>
          {!isCancelled && order.status !== 'ready' && order.status !== 'completed' && notifPermission !== 'granted' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-6 py-4 border-b border-[var(--glass-border)] bg-[var(--accent-10)]"
            >
              <button
                onClick={requestNotifications}
                className="w-full flex items-center justify-center gap-2 text-sm font-bold text-[var(--accent-color)]"
              >
                <BellRing className="w-4 h-4" />
                {t('tracker.notifyMe', language)}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Состав */}
        <div className="p-6 space-y-3">
          <p className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
            {t('tracker.items', language)}
          </p>
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm font-bold text-[var(--text-primary)]">
              <span>
                <span className="text-[var(--accent-color)] mr-2">x{item.quantity}</span>
                {item.names?.[language] || item.names?.ru || '—'}
              </span>
              <span className="whitespace-nowrap">
                {lineTotal(item)} {settings.currency}
              </span>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 bg-[var(--neutral-bg)] border-t border-[var(--glass-border)] flex justify-between items-center">
          <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-widest">
            {t('cashier.total', language)}
          </span>
          <span className="text-2xl font-black text-[var(--text-primary)]">
            {order.total} {settings.currency}
          </span>
        </div>

        <div className="p-6 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={async () => {
                const url = `${window.location.origin}/order/${order.id}`;
                const shareData = {
                  title: t('tracker.title', language),
                  text: `${t('tracker.title', language)} #${order.orderNumber || order.id.slice(-4)}`,
                  url,
                };
                try {
                  if (navigator.share) {
                    await navigator.share(shareData);
                  } else {
                    await navigator.clipboard.writeText(url);
                    toast.success(t('tracker.linkCopied', language));
                  }
                } catch (e: any) {
                  if (e?.name !== 'AbortError') {
                    try { await navigator.clipboard.writeText(url); toast.success(t('tracker.linkCopied', language)); } catch {}
                  }
                }
              }}
              className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-[var(--accent-color)] text-white font-bold text-sm shadow-md"
            >
              <Share2 className="w-4 h-4" />
              {t('tracker.share', language)}
            </button>
            <button
              onClick={() => navigate('/orders')}
              className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-[var(--bg-tertiary)] text-[var(--text-primary)] font-bold text-sm"
            >
              <History className="w-4 h-4" />
              {t('history.title', language)}
            </button>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-transparent text-[var(--text-secondary)] py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {t('checkout.backToMenu', language)}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderTracker;
